import { useFormField } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useClose } from '@calendly/ui/components/dropdown'
import { nanoid } from '@reduxjs/toolkit'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { listGroups } from 'features/user_groups'

import { GROUP_PAGE_SIZE } from '../constants'
import { defaultUserGroupSelector, userGroupsSelector, newUserGroupsSelector } from '../selectors'
import { addNewUserGroup, addUserGroup, editNewGroupName } from '../slice'
import { OptionsList } from './searchable-select-field'
import { StatusBadge, StyledCreateGroupOption, StyledSearchableSelectField } from './styles'

const searchUserGroups = async (query, setUserGroups) => {
  const [success, response] = await listGroups({ filter_term: query, page: 0, page_size: GROUP_PAGE_SIZE })

  if (success) {
    setUserGroups(response.collection)
  }
}

const generateNewGroup = (id, name) => (
  {
    id,
    originalName: name,
    isNewGroup: true,
    name: (
      <>
        {name}
        {' '}
        <StatusBadge>{I18n.t('publisher.organization_settings.user_provisioning.groups.calendly_group.new_label')}</StatusBadge>
      </>
    )
  }
)

const CreateGroupOption = ({ children, onClick }) => {
  const closeDropdown = useClose()
  const onClickHandler = () => {
    onClick?.(closeDropdown)
  }
  return (
    <StyledCreateGroupOption
      role="option"
      key="createGroup"
      onClick={onClickHandler}
    >
      {children}
    </StyledCreateGroupOption>
  )
}

export const GroupSelect = ({ groupId, ...props }) => {
  const dispatch = useDispatch()
  const [foundUserGroups, setFoundUserGroups] = useState([])
  const defaultUserGroup = useSelector(defaultUserGroupSelector)
  const existingUserGroups = useSelector(userGroupsSelector)
  const newUserGroups = useSelector(newUserGroupsSelector)
  const [query, setQuery] = useState('')
  const [_fieldProps, _meta, { setValue, setTouched }] = useFormField(props)

  const userGroups = useMemo(() => {
    const userGroupsToDisplay = foundUserGroups.length > 0 ? foundUserGroups : existingUserGroups
    const returnUserGroups = newUserGroups.concat(userGroupsToDisplay)
    if (defaultUserGroup) {
      const isDefaultUserGroupInGroups = returnUserGroups.find(({ id }) => id === defaultUserGroup.id)
      if (!isDefaultUserGroupInGroups) {
        returnUserGroups.push(defaultUserGroup)
      }
    }
    return returnUserGroups
  }, [newUserGroups, existingUserGroups, foundUserGroups, defaultUserGroup])

  const options = useMemo(() => (userGroups.map(({ id, name }) => ({ id, name }))), [userGroups])

  const [selectedGroup, setSelectedGroup] = useState(null)

  const defaultSearchQuery = selectedGroup?.originalName ? selectedGroup?.originalName : ''

  useEffect(() => {
    const userGroup = userGroups.find(({ id }) => id === groupId)
    if (userGroup) {
      setSelectedGroup(userGroup)
    } else {
      setSelectedGroup(null)
    }
  }, [userGroups, groupId])

  useEffect(() => {
    if (query === defaultSearchQuery) {
      setFoundUserGroups([])
      return
    }

    searchUserGroups(query, setFoundUserGroups)
  }, [query, setFoundUserGroups, defaultSearchQuery])

  const handleSearch = useCallback((searchQuery) => {
    setQuery(searchQuery)
  }, [setQuery])

  const handleCreateGroup = useCallback((closeDropdown) => {
    const userGroupExists = userGroups.find(({ name }) => name.toLowerCase?.() === query.toLowerCase())
    if (userGroupExists) {
      setValue(userGroupExists.id)
      setTouched(true)
      dispatch(addUserGroup(userGroupExists))
      closeDropdown()
      return
    }
    const newUserGroup = newUserGroups.find(
      (newGroup) => newGroup.originalName === query
    )
    if (newUserGroup) {
      setValue(newUserGroup.id)
      setTouched(true)
      closeDropdown()
      return
    }

    const newGroupId = nanoid(10)

    dispatch(addNewUserGroup(generateNewGroup(newGroupId, query)))
    setValue(newGroupId)
    setTouched(true)
    closeDropdown()
  }, [dispatch, query, setValue, setTouched, newUserGroups, userGroups])

  const onUpdate = useCallback(() => {
    let existingNewUserGroup = null
    let newUserGroup = null

    for (const newGroup of newUserGroups) {
      if (newGroup.originalName === query && !existingNewUserGroup) {
        existingNewUserGroup = newGroup
      }
      if (newGroup.id === groupId && !newUserGroup) {
        newUserGroup = newGroup
      }
      if (existingNewUserGroup && newUserGroup) {
        break
      }
    }

    if (!newUserGroup) return
    if (newUserGroup && existingNewUserGroup) return
    if (!query) return

    dispatch(editNewGroupName(generateNewGroup(groupId, query)))
  }, [dispatch, groupId, newUserGroups, query])


  const onSelectChange = (value) => {
    const userGroup = userGroups.find(({ id }) => id === value)
    if (!userGroup.isNewGroup) {
      dispatch(addUserGroup(userGroup))
    }
  }

  return (
    <StyledSearchableSelectField
      displayValue={selectedGroup?.name}
      placeholder={I18n.t('publisher.organization_settings.user_provisioning.groups.calendly_group.placeholder')}
      options={options}
      onSearch={handleSearch}
      defaultSearchQuery={defaultSearchQuery}
      onUpdate={onUpdate}
      onChange={onSelectChange}
      {...props}
    >
      <OptionsList
        notFoundText={I18n.t('publisher.organization.members.add_to_group.no_groups_found')}
      >
        {
          query
            ? (
              <CreateGroupOption
                role="option"
                key="createGroup"
                onClick={handleCreateGroup}
              >
                {I18n.t('publisher.organization_settings.user_provisioning.groups.create_group', { group_name: query })}
              </CreateGroupOption>
            )
            : null
        }
      </OptionsList>
    </StyledSearchableSelectField>
  )
}
